import React, { useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

import * as colors from '../../../../../common/styles/colors';
import type { ConsumptionEntry } from '../../../services/dataService';
import { colorForIndex } from '../../../utils/colors';
import { barThickness } from '../../../../../common/styles/global/chart.styles';
import { getCustomTooltipHandler } from './utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

type MultiSeries = Array<{ label: string; entries: ConsumptionEntry[] }>;

interface MonthlyChartProps {
  mode?: 'single' | 'multi';
  data?: ConsumptionEntry[]; // single MPAN
  series?: MultiSeries; // multi MPAN
  onMonthClick?: (yearMonth: string) => void; // YYYY-MM
  period?: '6' | '12' | '24';
  selectedMpans: string[];
}

function formatYearMonth(isoStart: string) {
  return isoStart.slice(0, 7); // YYYY-MM
}

const updateMonthLastLabel = (period: '6' | '12' | '24' | undefined, labels: string[]): string[] => {
  let expectedLastMonthLabel = '';
  if (period == '24' || period == '12' || period == '6') {
    const now = new Date();
    const month = now.toLocaleString(undefined, { month: 'short' });
    const year = now.getFullYear().toString().slice(-2);
    expectedLastMonthLabel = `${month} ${year}`;
  }

  // If last month label is missing, add it
  if (expectedLastMonthLabel && labels[labels.length - 1] !== expectedLastMonthLabel) {
    labels.push(expectedLastMonthLabel);
  }
  return labels;
};

const MonthlyChart: React.FC<MonthlyChartProps> = ({
  mode = 'single',
  data = [],
  series = [],
  onMonthClick,
  period,
  selectedMpans,
}) => {
  const chartRef = useRef<ChartJS>(null as any);

  const chartDataSingle = useMemo(() => {
    let labels = data.map(d => {
      const date = new Date(d.intervalStart);
      const month = date.toLocaleString(undefined, { month: 'short' });
      const year = date.getFullYear().toString().slice(-2); // 2-digit year
      return `${month} ${year}`;
    });

    labels = updateMonthLastLabel(period, labels);

    // If data only has totalConsumption just show that
    const hasOnlyTotal =
      data.length > 0 &&
      data.every(
        d =>
          d.totalConsumption !== undefined && d.actualConsumption === undefined && d.estimatedConsumption === undefined
      );
    if (hasOnlyTotal) {
      return {
        labels,
        datasets: [
          {
            label: 'Total Consumption',
            data: data.map(d => d.totalConsumption),
            backgroundColor: colors.hydroBlue,
            borderColor: colors.hydroBlue,
            borderWidth: 1,
            maxBarThickness: barThickness,
          },
        ],
      };
    }

    const now: Date = new Date();
    const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    const actual = data.map((d: ConsumptionEntry): number | null => {
      // Remove current month for month to date
      if (d.intervalStart.startsWith(currentYearMonth)) return null;

      if (d.actualConsumption > 0) return d.actualConsumption;

      // set value to `null` so it won't take space on graph
      return null;
    });

    // set value to `null` so it won't take space on graph
    const estimated = data.map((d: ConsumptionEntry): number | null => {
      // Remove current month for month to date
      if (d.intervalStart.startsWith(currentYearMonth)) return null;

      if (d.estimatedConsumption > 0) return d.estimatedConsumption;

      // set value to `null` so it won't take space on graph
      return null;
    });

    const currentMonthToDate = data.map((d: ConsumptionEntry): number | null => {
      if (d.intervalStart.startsWith(currentYearMonth)) return d.totalConsumption;

      // set value to `null` so it won't take space on graph
      return null;
    });

    return {
      labels,
      datasets: [
        {
          data: actual,
          skipNull: true,
          backgroundColor: colors.hydroBlue,
          borderColor: colors.hydroBlue,
          borderWidth: 1,
          maxBarThickness: barThickness,
          stack: 'stack0',
        },
        {
          data: estimated,
          skipNull: true,
          backgroundColor: colors.draxDarkBlue,
          borderColor: colors.draxDarkBlue,
          borderWidth: 1,
          maxBarThickness: barThickness,
          stack: 'stack0',
        },
        {
          data: currentMonthToDate,
          skipNull: true,
          backgroundColor: colors.successLight,
          borderColor: colors.successLight,
          borderWidth: 1,
          maxBarThickness: barThickness,
        },
      ],
    };
  }, [data]);

  const chartDataMulti = useMemo(() => {
    let labels = (series.find(s => s.entries?.length)?.entries || []).map(d => {
      const date = new Date(d.intervalStart);
      const month = date.toLocaleString(undefined, { month: 'short' });
      const year = date.getFullYear().toString().slice(-2); // 2-digit year
      return `${month} ${year}`;
    });

    labels = updateMonthLastLabel(period, labels);

    const datasets = (series || []).map((s, i) => {
      const { stroke, fill } = colorForIndex(i);
      return {
        label: s.label,
        data: (s.entries || []).map(d => d.totalConsumption),
        borderColor: stroke,
        backgroundColor: fill,
        tension: 0.2,
        pointRadius: 3,
      };
    });

    return { labels, datasets };
  }, [series]);

  const options = useMemo(() => {
    let title = 'Monthly Consumption';
    if (period === '6' || period === '12' || period === '24') {
      title = `Monthly Consumption - Last ${period} Months`;

      if (series && series.length > 0) title = `MPAN Comparison - Monthly Consumption - Last ${period} Months`;

      if (selectedMpans && selectedMpans.length === 1)
        title = `${selectedMpans[0]} - Monthly Consumption - Last ${period} Months`;
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: title,
          font: {
            size: 16, // Set the font size in pixels
            weight: 400,
            family: `Rubrik, 'Calibri'`,
          },
          color: colors.hydroBlue,
        },
        tooltip: {
          // https://www.chartjs.org/docs/latest/configuration/tooltip.html
          enabled: false,
          mode: 'point',
          external: getCustomTooltipHandler('kWh'),
        },
      },
      onClick: (_evt: unknown, elements: any[]) => {
        if (!onMonthClick || !elements?.length) return;
        const idx = elements[0].index;
        const entry = (mode === 'single' ? data : series.find(s => s.entries?.length)?.entries || [])[idx];
        if (entry) onMonthClick(formatYearMonth(entry.intervalStart));
      },
      scales: {
        x: {
          title: { display: true, text: 'Month', color: colors.hydroBlue },
          ticks: { minRotation: 90, maxRotation: 90, color: colors.hydroBlue },
          grid: { display: false },
        },
        y: {
          title: { display: true, text: 'kWh', color: colors.hydroBlue },
          ticks: { color: colors.hydroBlue },
          beginAtZero: true,
        },
      },
    };
  }, [data, series, onMonthClick, mode, period]);

  return (
    <div className='pl-3' style={{ height: 500 }}>
      {mode === 'single' ? (
        <Bar ref={chartRef as any} data={chartDataSingle} options={options as any} />
      ) : (
        <Line ref={chartRef as any} data={chartDataMulti} options={options as any} />
      )}
    </div>
  );
};

export default MonthlyChart;
